import { useCallback, useEffect, useMemo, useRef, useState } from "react";
import { Loader2 } from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { RoutePath } from "@/section/router";
import { FieldsList } from "./FieldsList";
import type { PdfField } from "../types";
import { useToast } from "@/components/ui/use-toast";
import {
  useGetAchTemplate,
  useGetAchTemplateDocument,
  type AchDocument,
} from "@/lib/api/ach";
import { safeParseDecimal } from "@/lib/api/common";
import {
  PdfDocumentViewer,
  PdfFieldOverlay,
  type PdfOverlayRender,
} from "@/section/pdf";
import { achFieldsToPdfFieldRects } from "./fieldTransforms";

const PDF_WIDTH = 610;

type TemplateFields = NonNullable<AchDocument["DocumentTemplate"]>["Fields"];

function normalizeTemplateFields(fields?: TemplateFields | null): PdfField[] {
  if (!Array.isArray(fields) || fields.length === 0) {
    return [];
  }

  return fields.map((rawField) => {
    const field = rawField as any;
    const position = field.position_data ?? {};
    const parseDecimal = safeParseDecimal({});
    const toNumber = (value: any) => {
      try {
        return parseDecimal(value ?? 0).toNumber();
      } catch {
        const numeric = Number(value);
        return Number.isFinite(numeric) ? numeric : 0;
      }
    };

    return {
      field_id: field.field_id,
      field_key: field.field_key,
      field_type: field.field_type === "signature" ? "signature" : "system",
      system_field_type: field.system_field_type,
      field_dest_type: field.field_dest_type,
      position_data: {
        x: toNumber(position.x),
        y: toNumber(position.y),
        width: toNumber(position.width),
        height: toNumber(position.height),
        text_size: Number(position.text_size ?? 12),
        page: Number(position.page ?? 1),
      },
      is_required: field.is_required,
      is_readonly: field.is_readonly,
      freeform_value: field.freeform_value,
      value:
        typeof field.freeform_value === "string"
          ? field.freeform_value
          : field.value ?? "",
    } satisfies PdfField;
  });
}

export function ViewAchPdfPreview() {
  const navigate = useNavigate();
  const { template_id } = useParams<{ template_id: string }>();
  const { toast } = useToast();
  const getTemplateDocument = useGetAchTemplateDocument();
  const getTemplateDocumentRef = useRef(getTemplateDocument);
  const achTemplateQuery = useGetAchTemplate({
    id: template_id ?? "",
    enabled: !!template_id,
  });

  const [pdfBytes, setPdfBytes] = useState<Uint8Array | null>(null);
  const [isLoadingPdf, setIsLoadingPdf] = useState(false);
  const [pdfError, setPdfError] = useState<string | null>(null);
  const [selectedFieldId, setSelectedFieldId] = useState<string | null>(null);

  const templateData = achTemplateQuery.data?.data as AchDocument | undefined;
  const templateFields = templateData?.DocumentTemplate?.Fields ?? [];

  const pdfFields = useMemo(
    () => normalizeTemplateFields(templateFields),
    [templateFields],
  );

  const pdfFieldRects = useMemo(
    () => achFieldsToPdfFieldRects(pdfFields),
    [pdfFields],
  );

  useEffect(() => {
    if (!pdfFields.length) {
      setSelectedFieldId(null);
      return;
    }

    setSelectedFieldId((current) => {
      if (current && pdfFields.some((field) => field.field_id === current)) {
        return current;
      }
      return pdfFields[0].field_id;
    });
  }, [pdfFields]);

  useEffect(() => {
    getTemplateDocumentRef.current = getTemplateDocument;
  }, [getTemplateDocument]);

  useEffect(() => {
    if (!template_id) {
      setPdfBytes(null);
      setPdfError("Missing ACH template id.");
      setIsLoadingPdf(false);
      return;
    }

    let isMounted = true;
    setIsLoadingPdf(true);
    setPdfError(null);
    setPdfBytes(null);

    (async () => {
      try {
        const result = await getTemplateDocumentRef.current(template_id);
        if (!isMounted) return;
        if (!result.success) {
          throw new Error("Failed to fetch template PDF metadata.");
        }

        const presignedUrl = result.data?.document_url;
        if (!presignedUrl) {
          throw new Error("Template PDF is unavailable.");
        }

        const response = await fetch(presignedUrl);
        if (!response.ok) {
          throw new Error("Unable to download template PDF.");
        }

        const buffer = await response.arrayBuffer();
        if (!isMounted) return;

        setPdfBytes(new Uint8Array(buffer));
      } catch (error: any) {
        if (!isMounted) return;
        const description =
          error?.message ?? "Unable to load template PDF.";
        setPdfError(description);
        toast({
          title: "Unable to load PDF",
          description,
          variant: "destructive",
        });
      } finally {
        if (!isMounted) return;
        setIsLoadingPdf(false);
      }
    })();

    return () => {
      isMounted = false;
    };
  }, [template_id, toast]);

  const templateTitle =
    templateData?.DocumentTemplate?.title ?? "Template Preview";

  const handleSelectField = useCallback((fieldId: string | null) => {
    setSelectedFieldId(fieldId);
  }, []);

  const overlayRenderer = useCallback<PdfOverlayRender>(
    (metrics) => {
      const pageFields = pdfFieldRects.filter(
        (field) => field.page === metrics.page,
      );
      if (pageFields.length === 0) {
        return null;
      }

      return (
        <PdfFieldOverlay
          fields={pageFields}
          metrics={metrics}
          selectedId={selectedFieldId ?? undefined}
          onSelect={(field) => handleSelectField(field.id)}
        />
      );
    },
    [handleSelectField, pdfFieldRects, selectedFieldId],
  );

  const isTemplateLoading = achTemplateQuery.isLoading;
  const templateError =
    achTemplateQuery.isError && !isTemplateLoading
      ? (achTemplateQuery.error as Error | undefined)?.message ??
        "Unable to load template metadata."
      : null;

  const fieldsPanel = (() => {
    if (!template_id) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
          No template selected.
        </div>
      );
    }

    if (isTemplateLoading) {
      return (
        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
          <Loader2 className="h-4 w-4 animate-spin" />
          Loading template fields…
        </div>
      );
    }

    if (templateError) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
          {templateError}
        </div>
      );
    }

    if (!pdfFields.length) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
          This template does not have any configured fields yet.
        </div>
      );
    }

    return (
      <FieldsList
        fields={pdfFields}
        selectedField={selectedFieldId}
        onSelectField={handleSelectField}
        onUpdateField={() => {}}
        onDeleteField={() => {}}
        mode={"display"}
      />
    );
  })();

  const pdfPanel = (() => {
    if (!template_id) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-muted-foreground">
          Select a template to view its PDF.
        </div>
      );
    }

    if (pdfError) {
      return (
        <div className="flex h-full items-center justify-center px-6 text-center text-sm text-destructive">
          {pdfError}
        </div>
      );
    }

    if (!pdfBytes) {
      return (
        <div className="flex h-full items-center justify-center gap-2 text-sm text-muted-foreground">
          {isLoadingPdf ? (
            <>
              <Loader2 className="h-4 w-4 animate-spin" />
              Loading PDF…
            </>
          ) : (
            "PDF is not available."
          )}
        </div>
      );
    }

    return (
      <PdfDocumentViewer
        source={pdfBytes}
        width={PDF_WIDTH}
        renderOverlay={overlayRenderer}
      />
    );
  })();

  return (
    <div className="flex h-full flex-col gap-4">
      <div className="flex flex-wrap items-center justify-between gap-3">
        <div className="flex items-center gap-3">
          <div>
            <p className="text-sm uppercase tracking-wide text-muted-foreground">
              ACH
            </p>
            <h2 className="text-2xl font-bold leading-tight">{templateTitle}</h2>
          </div>
        </div>
      </div>

      <div className="flex flex-1 overflow-hidden rounded-lg border bg-background shadow-sm max-h-[100%]">
        <div className="w-full border-r bg-muted/20 lg:w-1/3 overflow-y-scroll">{fieldsPanel}</div>
        <div className="flex-1 bg-muted/30">{pdfPanel}</div>
      </div>
    </div>
  );
}

